import type { CSSProperties } from "react";
import { Fragment } from "react";
import { useTranslations } from "use-intl";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Link } from "@/components/link";
import { AlertsCallout } from "@/components/messaging/alerts-callout";
import { EditorialBreadcrumbs } from "@/features/editorial-hub/editorial-breadcrumbs";
import {
  ArtistDirectory,
  type UpdateArtistDirectorySearch,
} from "@/features/feature-page/artist-directory";
import type { ArtistDirectorySearchState } from "@/features/feature-page/artist-directory-query";
import { CollectionPanel } from "@/features/feature-page/collection-panel";
import { FeaturePageBlock } from "@/features/feature-page/feature-page-block";
import { FeaturePageByline } from "@/features/feature-page/feature-page-byline";
import { FeaturePageCatalogProvider } from "@/features/feature-page/feature-page-catalog-context";
import { FeaturePageSectionNavigation } from "@/features/feature-page/feature-page-section-navigation";
import { FeaturePageSourceIndexContext } from "@/features/feature-page/feature-page-source-index-context";
import type { FeaturePageRenderModel } from "@garimpa/api/featurePages/types";
import { buildFeaturePageLandingPath } from "@garimpa/lib/buildFeaturePageLandingPath";

function ArtistDirectoryPage({
  page,
  search,
  updateSearch,
}: {
  page: FeaturePageRenderModel;
  search: ArtistDirectorySearchState;
  updateSearch: UpdateArtistDirectorySearch;
}) {
  const t = useTranslations("featurePageLanding");
  if (page.page.family !== "genre" && page.page.family !== "style") return null;
  const hero = page.blocks.find((block) => block.type === "hero");
  const remaining = page.blocks.filter((block) => block.id !== hero?.id);
  return (
    <div className="relative" style={{ "--feature-accent": page.theme.accent } as CSSProperties}>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 mx-auto max-w-[1440px] px-4 pt-6 md:px-10 lg:px-20">
        <Breadcrumbs
          className="pointer-events-auto"
          label={t("breadcrumbLabel")}
          separatorClassName="text-white/60"
        >
          <Link
            to={buildFeaturePageLandingPath(page.page.locale, page.page.family)}
            className="text-white/75 drop-shadow-sm transition-colors hover:text-white"
          >
            {page.page.family === "genre" ? t("genre.title") : t("style.title")}
          </Link>
          <span className="max-w-full truncate font-medium text-white drop-shadow-sm">
            {page.artistDirectory?.breadcrumbLabel ?? page.seo.title}
          </span>
        </Breadcrumbs>
      </div>
      {hero ? (
        <FeaturePageBlock
          block={hero}
          sources={page.sources}
          locale={page.page.locale}
          heroPresentation="directory"
        />
      ) : null}
      <ArtistDirectory
        family={page.page.family}
        subjectKey={page.page.subjectKey}
        heading={page.artistDirectory?.breadcrumbLabel ?? page.seo.title}
        search={search}
        updateSearch={updateSearch}
      />
      {remaining.map((block) => (
        <FeaturePageBlock
          key={block.id}
          block={block}
          sources={page.sources}
          locale={page.page.locale}
        />
      ))}
      <AlertsCallout surface={`${page.page.family}_page`} />
    </div>
  );
}

export function FeaturePageContent({
  page,
  directorySearch,
  updateDirectorySearch,
}: {
  page: FeaturePageRenderModel;
  directorySearch: ArtistDirectorySearchState;
  updateDirectorySearch: UpdateArtistDirectorySearch;
}) {
  if (page.page.family === "genre" || page.page.family === "style") {
    return (
      <FeaturePageCatalogProvider catalog={page.catalog}>
        <FeaturePageSourceIndexContext.Provider value={new Map()}>
          <ArtistDirectoryPage
            page={page}
            search={directorySearch}
            updateSearch={updateDirectorySearch}
          />
        </FeaturePageSourceIndexContext.Provider>
      </FeaturePageCatalogProvider>
    );
  }
  const sourceIndex = new Map(page.sources.map((source, index) => [source.id, index + 1]));
  const hero = page.blocks.find((block) => block.type === "hero");
  const pageTitle = hero?.title ?? page.seo.socialTitle;

  return (
    <FeaturePageCatalogProvider catalog={page.catalog}>
      <FeaturePageSourceIndexContext.Provider value={sourceIndex}>
        <article
          aria-labelledby={hero ? `${hero.id}-heading` : undefined}
          className="relative"
          style={{ "--feature-accent": page.theme.accent } as CSSProperties}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 mx-auto max-w-[1440px] px-4 pt-6 md:px-10 lg:px-20">
            <EditorialBreadcrumbs
              family={page.page.family}
              locale={page.page.locale}
              currentTitle={pageTitle}
              className="pointer-events-auto mb-0"
              overlay
            />
          </div>
          {page.blocks.map((block) => (
            <Fragment key={block.id}>
              <FeaturePageBlock block={block} sources={page.sources} locale={page.page.locale} />
              {block.type === "hero" ? (
                <>
                  <FeaturePageByline page={page.page} />
                  {page.collection ? <CollectionPanel collection={page.collection} /> : null}
                  <FeaturePageSectionNavigation blocks={page.blocks} />
                </>
              ) : null}
            </Fragment>
          ))}
        </article>
      </FeaturePageSourceIndexContext.Provider>
    </FeaturePageCatalogProvider>
  );
}
